import { useState } from 'react';
import { doc, updateDoc } from "firebase/firestore";
import { db } from '../../../firebase/firestore';
import { getDaysBetween, calculatePrice, getPlanName, getPlanId, getPlanPrice, formatDisplayDate } from '../utils/formatters';
import { updateCache } from '../utils/cache';

export const useSubscription = (users, setUsers, setFilteredUsers, showNotification) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [subscriptionAction, setSubscriptionAction] = useState('grant');
  const [subscriptionType, setSubscriptionType] = useState('manual');
  const [subscriptionDuration, setSubscriptionDuration] = useState('custom');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [loading, setLoading] = useState(false);

  const openSubscriptionModal = (user, action = 'grant') => {
    setSelectedUser(user);
    setSubscriptionAction(action);
    
    if (action === 'grant' || action === 'extend') {
      setSubscriptionType('manual');
      setSubscriptionDuration('custom');
      
      const now = new Date();
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 1);
      
      const formatDateForInput = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      setCustomStartDate(formatDateForInput(now));
      setCustomEndDate(formatDateForInput(endDate));
    }
    
    setIsSubscriptionModalOpen(true);
  };

  const closeSubscriptionModal = () => {
    setIsSubscriptionModalOpen(false);
    setSubscriptionAction('grant');
    setSubscriptionType('auto');
    setSubscriptionDuration('custom');
    setCustomStartDate('');
    setCustomEndDate('');
    setShowDatePicker(false);
    setSelectedUser(null);
  };

  // Получение имени коллекции в зависимости от типа пользователя
  const getCollectionName = (userType) => {
    return userType === 'web' ? 'webUsers' : 'telegramUsers';
  };

  const handleManageSubscription = async () => {
    if (!selectedUser) return;

    if (subscriptionDuration === 'custom') {
      if (!customStartDate || !customEndDate) {
        showNotification('error', 'Укажите даты начала и окончания');
        return;
      }
      
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      
      if (end <= start) {
        showNotification('error', 'Дата окончания должна быть позже даты начала');
        return;
      }
    }

    setLoading(true);
    
    try {
      const collectionName = getCollectionName(selectedUser.userType);
      const userRef = doc(db, collectionName, selectedUser.id);
      const now = new Date();
      let expiryDate;
      
      if (subscriptionDuration === 'custom') {
        expiryDate = new Date(customEndDate);
      } else {
        expiryDate = new Date();
        switch (subscriptionDuration) {
          case '1': expiryDate.setMonth(expiryDate.getMonth() + 1); break;
          case '3': expiryDate.setMonth(expiryDate.getMonth() + 3); break;
          case '6': expiryDate.setMonth(expiryDate.getMonth() + 6); break;
          default: expiryDate.setMonth(expiryDate.getMonth() + 1);
        }
      }

      let updateData = {};
      
      if (subscriptionAction === 'grant' || subscriptionAction === 'extend') {
        let finalExpiryDate;
        
        if (subscriptionAction === 'extend' && selectedUser.premiumExpiresAt) {
          const currentExpiry = new Date(selectedUser.premiumExpiresAt);
          if (currentExpiry > now) {
            finalExpiryDate = new Date(currentExpiry);
            
            if (subscriptionDuration === 'custom') {
              const daysToAdd = getDaysBetween(now, expiryDate);
              finalExpiryDate.setDate(finalExpiryDate.getDate() + daysToAdd);
            } else {
              switch (subscriptionDuration) {
                case '1': finalExpiryDate.setMonth(finalExpiryDate.getMonth() + 1); break;
                case '3': finalExpiryDate.setMonth(finalExpiryDate.getMonth() + 3); break;
                case '6': finalExpiryDate.setMonth(finalExpiryDate.getMonth() + 6); break;
              }
            }
          } else {
            finalExpiryDate = expiryDate;
          }
        } else {
          finalExpiryDate = expiryDate;
        }

        updateData = {
          premium: true,
          premiumActivatedAt: subscriptionAction === 'grant' ? now.toISOString() : selectedUser.premiumActivatedAt || now.toISOString(),
          premiumExpiresAt: finalExpiryDate.toISOString(),
          premiumActivationType: subscriptionType,
          updatedAt: now.toISOString(),
          subscription: {
            active: true,
            status: 'active',
            planName: subscriptionDuration === 'custom' 
              ? `Custom (${getDaysBetween(now, finalExpiryDate)} days)` 
              : getPlanName(subscriptionDuration),
            planId: subscriptionDuration === 'custom' ? 'custom' : getPlanId(subscriptionDuration),
            price: subscriptionDuration === 'custom' 
              ? calculatePrice(getDaysBetween(now, finalExpiryDate))
              : getPlanPrice(subscriptionDuration),
            interval: subscriptionDuration === 'custom' ? 'custom' : 
                     subscriptionDuration === '1' ? 'month' : 
                     subscriptionDuration === '3' ? '3_months' : '6_months',
            currentPeriodStart: now.toISOString(),
            currentPeriodEnd: finalExpiryDate.toISOString(),
            activationType: subscriptionType,
            activatedBy: 'admin',
            activatedAt: now.toISOString()
          }
        };

        if (subscriptionType === 'manual') {
          updateData.subscription.manualActivation = {
            activatedBy: 'admin',
            activatedAt: now.toISOString(),
            duration: subscriptionDuration,
            startDate: customStartDate || now.toISOString(),
            endDate: finalExpiryDate.toISOString(),
            reason: subscriptionAction === 'extend' ? 'extension' : 'manual_grant'
          };
        }
        
        showNotification('success', 
          subscriptionAction === 'extend' 
            ? `Подписка продлена до ${formatDisplayDate(finalExpiryDate)}` 
            : `Premium доступ предоставлен до ${formatDisplayDate(finalExpiryDate)}`
        );
        
      } else if (subscriptionAction === 'revoke') {
        updateData = {
          premium: false,
          premiumExpiresAt: null,
          updatedAt: now.toISOString(),
          subscription: {
            ...selectedUser.subscription,
            active: false,
            status: 'canceled',
            canceledAt: now.toISOString(),
            canceledBy: 'admin'
          }
        };
        
        showNotification('success', 'Premium доступ отозван');
      }

      await updateDoc(userRef, updateData);
      updateCache(selectedUser.id, updateData);
      
      setUsers(prev => prev.map(u => u.id === selectedUser.id ? { ...u, ...updateData } : u));
      setFilteredUsers(prev => prev.map(u => u.id === selectedUser.id ? { ...u, ...updateData } : u));
      
      closeSubscriptionModal();
      
    } catch (error) {
      console.error("Ошибка управления подпиской:", error);
      showNotification('error', `Ошибка: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return {
    subscriptionState: {
      selectedUser,
      isSubscriptionModalOpen,
      subscriptionAction,
      subscriptionType,
      subscriptionDuration,
      customStartDate,
      customEndDate,
      showDatePicker,
      loading
    },
    setSubscriptionType,
    setSubscriptionDuration,
    setCustomStartDate,
    setCustomEndDate,
    setShowDatePicker,
    openSubscriptionModal,
    closeSubscriptionModal,
    handleManageSubscription
  };
};